// Safari only autoplays when the muted *property* is set before play(). Under iOS Low Power Mode or
// "Never Auto-Play" it refuses play() until a tap, and unlocks each video separately, so every tap
// unlocks all still-locked videos (pausing the off-screen ones) and they then start as they scroll in.
// Quinn's unmuted overlay player sits outside sections and has no autoplay attribute, so it is never touched.
// [data-lazy-autoplay] videos carry preload="none" and no autoplay attribute: they load and start only once visible.
const AUTOPLAY_VIDEO_SELECTOR = '[id^="shopify-section-"] video[autoplay], [data-lazy-autoplay] video';
const UNLOCK_VIDEO_SELECTOR = `${AUTOPLAY_VIDEO_SELECTOR}, [data-hero-video] video`;
const GESTURE_EVENTS = ['touchend', 'click', 'keydown'];

document.addEventListener('DOMContentLoaded', () => {
  const tracked = new Set();
  const inView = new Set();
  const unlocked = new WeakSet();

  const tryPlay = (video) => {
    if (!video.paused) return;
    video.muted = true;
    video.play().catch(() => {});
  };

  const viewObserver = new IntersectionObserver(
    (entries) => {
      for (const { target, isIntersecting } of entries) {
        if (!isIntersecting) {
          inView.delete(target);
          continue;
        }
        inView.add(target);
        // Hero videos are started and stopped by hero-carousel.js; only track their visibility here.
        if (target.matches(AUTOPLAY_VIDEO_SELECTOR)) tryPlay(target);
      }
    },
    { threshold: 0.25 },
  );

  const isAutoplay = (video) => video.matches(AUTOPLAY_VIDEO_SELECTOR);
  const resumeInView = () => {
    if (document.hidden) return;
    for (const video of inView) if (isAutoplay(video)) tryPlay(video);
  };

  const track = (video) => {
    if (tracked.has(video)) return;
    tracked.add(video);
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    viewObserver.observe(video);

    // Quinn attaches the file only once the card is visible; play() before that fails and is not retried.
    for (const type of ['loadeddata', 'canplay']) {
      video.addEventListener(type, () => inView.has(video) && isAutoplay(video) && tryPlay(video));
    }

    // Safari pauses visible videos to save power; restart theme videos. App videos manage their own pauses.
    if (!video.closest('.shopify-app-block')) {
      video.addEventListener('pause', () => {
        if (!inView.has(video) || document.hidden || !isAutoplay(video)) return;
        setTimeout(() => tryPlay(video), 250);
      });
    }
  };

  const scan = (root) => {
    if (root.matches?.(UNLOCK_VIDEO_SELECTOR)) track(root);
    for (const video of root.querySelectorAll?.(UNLOCK_VIDEO_SELECTOR) ?? []) track(video);
  };

  scan(document);

  new MutationObserver((mutations) => {
    for (const { type, target, addedNodes } of mutations) {
      if (type === 'attributes') scan(target);
      for (const node of addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) scan(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['autoplay'] });

  // play() must be called synchronously inside the gesture for Safari to unlock that element.
  const unlockAll = () => {
    for (const video of tracked) {
      // Skip display:none variants (e.g. the desktop video on mobile) so a tap never downloads them.
      if (unlocked.has(video) || !video.isConnected || !video.getClientRects().length) continue;
      const wasPlaying = !video.paused;
      video.muted = true;
      video
        .play()
        .then(() => {
          unlocked.add(video);
          if (!wasPlaying && !inView.has(video)) video.pause();
        })
        .catch(() => {});
    }
  };

  for (const type of GESTURE_EVENTS) document.addEventListener(type, unlockAll, { passive: true });

  // Back/forward cache restores and tab switches leave videos paused while already in view.
  window.addEventListener('pageshow', resumeInView);
  document.addEventListener('visibilitychange', resumeInView);
});
